import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { RaceTransferRuleSortingType } from './../enums/RaceTransferRuleSortingType';

export class RaceTransferRule extends BaseObj {
    public ID: number;
    public __id: number;

    public CategoryID: number | null;
    public __categoryid: number | null;

    public Count: number;
    public __count: number;

    public EventID: number;
    public __eventid: number;

    public FromLevelID: number | null;
    public __fromlevelid: number | null;

    public FromRaceID: number | null;
    public __fromraceid: number | null;

    public MarkAsLuckyLoser: boolean;
    public __markasluckyloser: boolean;

    public PursuitFactor: number;
    public __pursuitfactor: number;

    public RaceID: number;
    public __raceid: number;

    public SkipTransferred: boolean;
    public __skiptransferred: boolean;

    public Sort: number;
    public __sort: number;

    public SortingType: RaceTransferRuleSortingType;
    public __sortingtype: RaceTransferRuleSortingType;

    public StartFrom: number;
    public __startfrom: number;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        CategoryID?: number | null;
        Count?: number;
        EntityState?: EntityState;
        EventID?: number;
        FromLevelID?: number | null;
        FromRaceID?: number | null;
        MarkAsLuckyLoser?: boolean;
        PursuitFactor?: number;
        RaceID?: number;
        SkipTransferred?: boolean;
        Sort?: number;
        SortingType?: RaceTransferRuleSortingType;
        StartFrom?: number;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<RaceTransferRule>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('RaceTransferRule tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryID = values.CategoryID ?? null;
        this.__categoryid = values.CategoryID ?? null;
        Object.defineProperty(this, 'CategoryID', {
            get(): number | null {
                return this.__categoryid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('CategoryID', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Count = values.Count ?? 0;
        this.__count = values.Count ?? 0;
        Object.defineProperty(this, 'Count', {
            get(): number {
                return this.__count;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Count', this.__count, value);
                this.__count = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FromLevelID = values.FromLevelID ?? null;
        this.__fromlevelid = values.FromLevelID ?? null;
        Object.defineProperty(this, 'FromLevelID', {
            get(): number | null {
                return this.__fromlevelid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('FromLevelID', this.__fromlevelid, value);
                this.__fromlevelid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FromRaceID = values.FromRaceID ?? null;
        this.__fromraceid = values.FromRaceID ?? null;
        Object.defineProperty(this, 'FromRaceID', {
            get(): number | null {
                return this.__fromraceid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('FromRaceID', this.__fromraceid, value);
                this.__fromraceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MarkAsLuckyLoser = values.MarkAsLuckyLoser ?? false;
        this.__markasluckyloser = values.MarkAsLuckyLoser ?? false;
        Object.defineProperty(this, 'MarkAsLuckyLoser', {
            get(): boolean {
                return this.__markasluckyloser;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('MarkAsLuckyLoser', this.__markasluckyloser, value);
                this.__markasluckyloser = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PursuitFactor = values.PursuitFactor ?? 0;
        this.__pursuitfactor = values.PursuitFactor ?? 0;
        Object.defineProperty(this, 'PursuitFactor', {
            get(): number {
                return this.__pursuitfactor;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('PursuitFactor', this.__pursuitfactor, value);
                this.__pursuitfactor = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SkipTransferred = values.SkipTransferred ?? false;
        this.__skiptransferred = values.SkipTransferred ?? false;
        Object.defineProperty(this, 'SkipTransferred', {
            get(): boolean {
                return this.__skiptransferred;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('SkipTransferred', this.__skiptransferred, value);
                this.__skiptransferred = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sort = values.Sort ?? 0;
        this.__sort = values.Sort ?? 0;
        Object.defineProperty(this, 'Sort', {
            get(): number {
                return this.__sort;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Sort', this.__sort, value);
                this.__sort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SortingType = values.SortingType ?? 'RankAscending';
        this.__sortingtype = values.SortingType ?? 'RankAscending';
        Object.defineProperty(this, 'SortingType', {
            get(): RaceTransferRuleSortingType {
                return this.__sortingtype;
            },
            set(value: RaceTransferRuleSortingType) {
                this.__entityStateHandler.propertyChanged('SortingType', this.__sortingtype, value);
                this.__sortingtype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartFrom = values.StartFrom ?? 0;
        this.__startfrom = values.StartFrom ?? 0;
        Object.defineProperty(this, 'StartFrom', {
            get(): number {
                return this.__startfrom;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StartFrom', this.__startfrom, value);
                this.__startfrom = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"Count": ${JSON.stringify(this.Count)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"FromLevelID": ${JSON.stringify(this.FromLevelID)}` +
            ',' +
            `"FromRaceID": ${JSON.stringify(this.FromRaceID)}` +
            ',' +
            `"MarkAsLuckyLoser": ${JSON.stringify(this.MarkAsLuckyLoser)}` +
            ',' +
            `"PursuitFactor": ${JSON.stringify(this.PursuitFactor)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"SkipTransferred": ${JSON.stringify(this.SkipTransferred)}` +
            ',' +
            `"Sort": ${JSON.stringify(this.Sort)}` +
            ',' +
            `"SortingType": ${this.SortingType == 'RankAscending' ? 1 : this.SortingType == 'RankDescending' ? 2 : this.SortingType == 'BibAscending' ? 3 : this.SortingType == 'BibDescending' ? 4 : this.SortingType == 'Random' ? 5 : this.SortingType == 'PointsInLevelDescending' ? 6 : this.SortingType == 'StartOrder' ? 7 : this.SortingType == 'RankLessThan5InHeatThenByTimeInProlog' ? 8 : this.SortingType == 'RankLessThan4InHeatThenByTimeInProlog' ? 9 : 1}` +
            ',' +
            `"StartFrom": ${JSON.stringify(this.StartFrom)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of RaceTransferRule from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        CategoryID?: number | null;
        Count?: number;
        EventID?: number;
        FromLevelID?: number | null;
        FromRaceID?: number | null;
        MarkAsLuckyLoser?: boolean;
        PursuitFactor?: number;
        RaceID?: number;
        SkipTransferred?: boolean;
        Sort?: number;
        SortingType?: RaceTransferRuleSortingType;
        StartFrom?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): RaceTransferRule {
        return new RaceTransferRule(values);
    }

    public static _FromServerJson(json: any): RaceTransferRule {
        return new RaceTransferRule({
            ID: json.ID,
            CategoryID: json.CategoryID ?? null,
            Count: json.Count,
            EventID: json.EventID,
            FromLevelID: json.FromLevelID ?? null,
            FromRaceID: json.FromRaceID ?? null,
            MarkAsLuckyLoser: json.MarkAsLuckyLoser,
            PursuitFactor: json.PursuitFactor,
            RaceID: json.RaceID,
            SkipTransferred: json.SkipTransferred,
            Sort: json.Sort,
            SortingType:
                json.SortingType == 1
                    ? 'RankAscending'
                    : json.SortingType == 2
                      ? 'RankDescending'
                      : json.SortingType == 3
                        ? 'BibAscending'
                        : json.SortingType == 4
                          ? 'BibDescending'
                          : json.SortingType == 5
                            ? 'Random'
                            : json.SortingType == 6
                              ? 'PointsInLevelDescending'
                              : json.SortingType == 7
                                ? 'StartOrder'
                                : json.SortingType == 8
                                  ? 'RankLessThan5InHeatThenByTimeInProlog'
                                  : json.SortingType == 9
                                    ? 'RankLessThan4InHeatThenByTimeInProlog'
                                    : 'RankAscending',
            StartFrom: json.StartFrom,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of RaceTransferRule from another instance of RaceTransferRule.
     * @param other The RaceTransferRule you want to clone
     * @returns A cloned RaceTransferRule
     */
    public static FromOther(other: RaceTransferRule): RaceTransferRule {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return RaceTransferRule._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RaceTransferRule.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'CategoryID'
            | 'Count'
            | 'EventID'
            | 'FromLevelID'
            | 'FromRaceID'
            | 'MarkAsLuckyLoser'
            | 'PursuitFactor'
            | 'RaceID'
            | 'SkipTransferred'
            | 'Sort'
            | 'SortingType'
            | 'StartFrom'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'RaceTransferRule';
}
